import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Col, Container, Row, Spinner } from 'reactstrap';
import {
	ArrowLeft,
	BadgePercent,
	Building2,
	CalendarDays,
	CheckCircle2,
	Clock,
	Edit3,
	Image as ImageIcon,
	Users,
	XCircle,
} from 'lucide-react';
import withAuth from '../../../hocs/withAuth';
import {
	cancelGroupDeal,
	fetchGroupDealDetails,
	patchGroupDeal,
	publishGroupDeal,
} from '../../../store/actions/deals';
import NavbarBusiness from '../../Components/BusinessNavbar';
import BusinessSidebar from '../../Components/BusinessSidebar';
import DealFormModal from '../DealFormModal';
import '../DealsPage/index.css';
import './index.css';

const formatDate = (date) => {
	if (!date) return 'No date';

	return new Date(date).toLocaleString('en-CA', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
};

const getStatusClass = (status) => {
	switch (status) {
		case 'published':
			return 'deal-card__status deal-card__status--published';
		case 'draft':
			return 'deal-card__status deal-card__status--draft';
		case 'confirmed':
			return 'deal-card__status deal-card__status--confirmed';
		case 'full':
			return 'deal-card__status deal-card__status--full';
		case 'cancelled':
			return 'deal-card__status deal-card__status--cancelled';
		case 'expired':
			return 'deal-card__status deal-card__status--expired';
		case 'completed':
			return 'deal-card__status deal-card__status--completed';
		default:
			return 'deal-card__status';
	}
};

const getDiscountPercent = (deal) => {
	if (!deal?.originalPrice || deal?.discountedPrice === undefined) return null;

	return Math.round(
		((deal.originalPrice - deal.discountedPrice) / deal.originalPrice) * 100,
	);
};

const DetailMetric = ({ icon, label, value }) => (
	<div className='deal-detail__metric'>
		<div className='deal-detail__metric-icon'>{icon}</div>
		<div>
			<div className='deal-detail__metric-label'>{label}</div>
			<div className='deal-detail__metric-value'>{value}</div>
		</div>
	</div>
);

const DealDetailsPage = () => {
	const { dealId } = useParams();
	const dispatch = useDispatch();
	const [isEditOpen, setIsEditOpen] = useState(false);

	const currentUser = useSelector((state) => state.currentUser);
	const businessId = currentUser?.user?.id;
	const { currentDeal, list, loading, error } = useSelector(
		(state) => state.groupDeals,
	);

	const deal = useMemo(() => {
		if (currentDeal?._id === dealId) return currentDeal;
		return list.find((item) => item._id === dealId) || null;
	}, [currentDeal, dealId, list]);

	useEffect(() => {
		if (businessId && dealId) {
			dispatch(fetchGroupDealDetails(businessId, dealId));
		}
	}, [businessId, dealId, dispatch]);

	const canEdit = deal && ['draft', 'published'].includes(deal.status);
	const canPublish = deal?.status === 'draft';
	const canCancel =
		deal && !['cancelled', 'completed'].includes(deal.status || 'draft');
	const discountPercent = getDiscountPercent(deal);

	const handlePublish = async () => {
		if (!deal || !window.confirm('Publish this deal to residents?')) return;
		await dispatch(publishGroupDeal(businessId, deal._id));
	};

	const handleCancel = async () => {
		if (!deal || !window.confirm('Cancel this deal?')) return;
		await dispatch(cancelGroupDeal(businessId, deal._id));
	};

	const handleEditSubmit = (payload) =>
		dispatch(patchGroupDeal(businessId, deal._id, payload));

	return (
		<div className='business-page-shell'>
			<NavbarBusiness />

			<Row className='g-0 no-gutters'>
				<Col xs='12' md='3' lg='3'>
					<BusinessSidebar />
				</Col>

				<Col xs='12' md='9' lg='9' className='business-page-shell__main'>
					<Container fluid className='deal-detail'>
						<Link to='/deals' className='deal-detail__back'>
							<ArrowLeft size={17} />
							Back to deals
						</Link>

						{loading && !deal ? (
							<div className='deal-detail__loading'>
								<Spinner size='sm' />
								<span>Loading deal details...</span>
							</div>
						) : null}

						{error && !deal ? (
							<div className='deal-detail__empty'>
								<XCircle size={28} />
								<h3>Deal could not be loaded</h3>
								<p>{error}</p>
							</div>
						) : null}

						{deal ? (
							<>
								<div className='deal-detail__header'>
									<div>
										<div className='deal-detail__eyebrow'>Group Deal</div>
										<h1 className='deal-detail__title'>
											{deal.title || 'Untitled deal'}
										</h1>
										<span className={getStatusClass(deal.status)}>
											{deal.status || 'draft'}
										</span>
									</div>

									<div className='deal-detail__actions'>
										{canEdit ? (
											<button
												type='button'
												className='deal-detail__action-btn'
												onClick={() => setIsEditOpen(true)}
												disabled={loading}>
												<Edit3 size={16} />
												Edit
											</button>
										) : null}

										{canPublish ? (
											<button
												type='button'
												className='deal-detail__action-btn deal-detail__action-btn--primary'
												onClick={handlePublish}
												disabled={loading}>
												<CheckCircle2 size={16} />
												Publish
											</button>
										) : null}

										{canCancel ? (
											<button
												type='button'
												className='deal-detail__action-btn deal-detail__action-btn--danger'
												onClick={handleCancel}
												disabled={loading}>
												<XCircle size={16} />
												Cancel
											</button>
										) : null}
									</div>
								</div>

								<div className='deal-detail__metrics'>
									<DetailMetric
										icon={<BadgePercent size={19} />}
										label='Price'
										value={`CD$ ${deal.discountedPrice || 0}`}
									/>
									<DetailMetric
										icon={<Users size={19} />}
										label='Bookings'
										value={`${deal.bookedCount || 0} / ${deal.maxBookings || 0}`}
									/>
									<DetailMetric
										icon={<Clock size={19} />}
										label='Minimum'
										value={`${deal.minBookingsRequired || 0} required`}
									/>
									<DetailMetric
										icon={<CalendarDays size={19} />}
										label='Service'
										value={formatDate(deal.serviceDate)}
									/>
								</div>

								<Row>
									<Col lg='8'>
										<section className='deal-detail__section'>
											<div className='deal-detail__section-header'>
												<h2>Offer Details</h2>
												{discountPercent ? (
													<Badge className='deal-card__discount-badge'>
														{discountPercent}% OFF
													</Badge>
												) : null}
											</div>

											<p className='deal-detail__description'>
												{deal.description || 'No description available.'}
											</p>

											<div className='deal-detail__price-row'>
												<div>
													<span>Original price</span>
													<strong>CD$ {deal.originalPrice || 0}</strong>
												</div>
												<div>
													<span>Discounted price</span>
													<strong>CD$ {deal.discountedPrice || 0}</strong>
												</div>
											</div>
										</section>

										<section className='deal-detail__section'>
											<div className='deal-detail__section-header'>
												<h2>Images</h2>
											</div>

											{Array.isArray(deal.images) && deal.images.length > 0 ? (
												<div className='deal-detail__image-grid'>
													{deal.images.map((image) => (
														<img key={image} src={image} alt={deal.title} />
													))}
												</div>
											) : (
												<div className='deal-detail__muted-row'>
													<ImageIcon size={18} />
													No images added yet.
												</div>
											)}
										</section>

										<section className='deal-detail__section'>
											<div className='deal-detail__section-header'>
												<h2>Terms</h2>
											</div>
											<p className='deal-detail__body-text'>
												{deal.terms || 'No terms added.'}
											</p>

											<div className='deal-detail__divider' />

											<h3>Cancellation Policy</h3>
											<p className='deal-detail__body-text'>
												{deal.cancellationPolicy ||
													'No cancellation policy added.'}
											</p>
										</section>
									</Col>

									<Col lg='4'>
										<section className='deal-detail__section'>
											<div className='deal-detail__section-header'>
												<h2>Schedule</h2>
											</div>
											<div className='deal-detail__timeline'>
												<div>
													<span>Booking deadline</span>
													<strong>{formatDate(deal.bookingDeadline)}</strong>
												</div>
												<div>
													<span>Service date</span>
													<strong>{formatDate(deal.serviceDate)}</strong>
												</div>
											</div>
										</section>

										<section className='deal-detail__section'>
											<div className='deal-detail__section-header'>
												<h2>Target Buildings</h2>
											</div>

											<div className='deal-detail__building-list'>
												{deal.targetBuildings?.map((target) => {
													const building = target.building;
													return (
														<div
															className='deal-detail__building'
															key={building?._id || building}>
															<Building2 size={17} />
															<span>
																{building?.address || 'Selected building'}
															</span>
														</div>
													);
												})}
											</div>
										</section>

										<section className='deal-detail__section'>
											<div className='deal-detail__section-header'>
												<h2>Booked Residents</h2>
											</div>

											{deal.bookedResidents?.length ? (
												<div className='deal-detail__resident-list'>
													{deal.bookedResidents.map((resident) => (
														<div
															className='deal-detail__resident'
															key={resident._id}>
															<div className='deal-detail__resident-avatar'>
																{resident.image ? (
																	<img
																		src={resident.image}
																		alt={`${resident.firstName || ''} ${
																			resident.lastName || ''
																		}`}
																	/>
																) : (
																	<Users size={16} />
																)}
															</div>
															<div>
																<strong>
																	{resident.firstName || 'Resident'}{' '}
																	{resident.lastName || ''}
																</strong>
																<span>{resident.unit || 'No unit listed'}</span>
															</div>
														</div>
													))}
												</div>
											) : (
												<div className='deal-detail__muted-row'>
													<Users size={18} />
													No residents booked yet.
												</div>
											)}
										</section>
									</Col>
								</Row>

								<DealFormModal
									isOpen={isEditOpen}
									toggle={() => setIsEditOpen((prev) => !prev)}
									mode='edit'
									initialDeal={deal}
									onSubmit={handleEditSubmit}
								/>
							</>
						) : null}
					</Container>
				</Col>
			</Row>
		</div>
	);
};

export default withAuth(DealDetailsPage);
